// [목적] collection 도메인 REST API 클라이언트 — /collections/* 8개 엔드포인트 매핑
// [이유] fe-be-wire-up Wave 0 INFRA-06 — CollectionPage 실데이터 연결 준비.
//         컬렉션 CRUD + 아이템 add/list/remove 를 단일 모듈로 캡슐화
// [사이드 이팩트] GET /collections · /collections/:id · /collections/:id/items: AnonOrAuthenticatedGuard (비인증 허용).
//                 POST/PATCH/DELETE: JwtAuthGuard + CsrfGuard 필수 → http.ts 가 CSRF 헤더 자동 첨부.
//                 softDelete / removeItem: 204 반환 → http.ts undefined 반환.
//                 POST /collections/:id/items body 는 { portfolioId: UUID } (addCollectionItemInputSchema).
// [수정 시 고려사항] collectionId · folioId 는 UUID 형식 (BE ParseUUIDPipe). 잘못된 UUID 시 400 반환.
//                   private 컬렉션 조회 시 비큐레이터는 404 반환 (R-9 정보 누설 방지).
//                   kind/slug 변경 시 BE 가 strict 400 반환 (patchCollectionInputSchema).

import {
  collectionSchema,
  collectionDetailSchema,
  collectionListQuerySchema,
  createCollectionInputSchema,
  patchCollectionInputSchema,
  addCollectionItemInputSchema,
  collectionItemPreviewSchema,
  type Collection,
  type CollectionDetail,
  type CollectionListQuery,
  type CreateCollectionInput,
  type PatchCollectionInput,
  type CollectionItemPreview,
} from '@gangwoncanvas/types';
import { z } from 'zod';
import { fetchWithEnvelope } from './http.js';

// ─── 내부 헬퍼 ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ─── 페이지네이션 응답 스키마 ──────────────────────────────────────────────────

const collectionPageSchema = z.object({
  items: z.array(collectionSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().optional(),
});

const collectionItemPageSchema = z.object({
  items: z.array(collectionItemPreviewSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().optional(),
});

export type CollectionPage = z.infer<typeof collectionPageSchema>;
export type CollectionItemPage = z.infer<typeof collectionItemPageSchema>;

// ─── API 메서드 ────────────────────────────────────────────────────────────────

/**
 * [목적] GET /collections — 공개 컬렉션 목록 (cursor 페이지네이션)
 * [이유] INFRA-06: ExplorePage · CollectionPage 탐색에서 호출. 비인증 허용
 * [사이드 이팩트] kind='editor' 필터로 에디터 큐레이션 컬렉션 전용 조회 가능
 * [수정 시 고려사항] curatorId UUID 필터로 특정 큐레이터의 컬렉션 목록 조회 가능
 */
export async function list(q?: Partial<CollectionListQuery>): Promise<CollectionPage> {
  const validated = q ? collectionListQuerySchema.partial().parse(q) : {};
  const query = buildQuery(validated as Record<string, string | number | undefined>);
  const data = await fetchWithEnvelope<unknown>(`/collections${query}`);
  return collectionPageSchema.parse(data);
}

/**
 * [목적] GET /collections/:id — 컬렉션 상세 + itemsPreview(top 20)
 * [이유] INFRA-06: CollectionPage 진입 시 호출. AnonOrAuthenticatedGuard 적용
 * [사이드 이팩트] private + 비큐레이터 접근 시 404 반환 (정보 누설 방지 R-9)
 * [수정 시 고려사항] collectionDetailSchema 는 itemsPreview 배열 포함 (상세 + 아이템 일괄)
 */
export async function getById(id: string): Promise<CollectionDetail> {
  const data = await fetchWithEnvelope<unknown>(`/collections/${id}`);
  return collectionDetailSchema.parse(data);
}

/**
 * [목적] POST /collections — 컬렉션 생성 (curator 본인, 인증 필수)
 * [이유] INFRA-06: 큐레이터 컬렉션 생성 화면에서 호출 (후속 UI 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. kind='editor' 서버 강제 (클라이언트 지정 불가)
 * [수정 시 고려사항] slug 서버 자동 생성 (클라이언트 미입력). createCollectionInputSchema 로 선검증
 */
export async function create(body: CreateCollectionInput): Promise<Collection> {
  const validated = createCollectionInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>('/collections', {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return collectionSchema.parse(data);
}

/**
 * [목적] PATCH /collections/:id — 컬렉션 수정 (curator 본인만)
 * [이유] INFRA-06: 컬렉션 편집 화면에서 호출 (후속 UI 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. slug/kind 변경 포함 시 BE strict 400
 * [수정 시 고려사항] patchCollectionInputSchema.strict() — 추가 필드 400 반환
 */
export async function patch(id: string, body: PatchCollectionInput): Promise<Collection> {
  const validated = patchCollectionInputSchema.parse(body);
  const data = await fetchWithEnvelope<unknown>(`/collections/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(validated),
  });
  return collectionSchema.parse(data);
}

/**
 * [목적] DELETE /collections/:id — 컬렉션 soft-delete (204 반환)
 * [이유] INFRA-06: 컬렉션 삭제 액션에서 호출 (후속 UI 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. 204 → http.ts undefined 반환
 * [수정 시 고려사항] soft-delete: DB deletedAt 갱신. 영구 삭제 아님
 */
export async function softDelete(id: string): Promise<void> {
  await fetchWithEnvelope<void>(`/collections/${id}`, { method: 'DELETE' });
}

/**
 * [목적] POST /collections/:id/items — 폴리오 컬렉션 추가 (idempotent)
 * [이유] INFRA-06: 컬렉션에 폴리오 추가 버튼에서 호출 (후속 UI 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. body 는 { portfolioId: UUID } (addCollectionItemInputSchema)
 * [수정 시 고려사항] private 폴리오 추가 차단 → 400. 동일 폴리오 재추가 → idempotent 200
 */
export async function addItem(id: string, folioId: string): Promise<CollectionItemPreview> {
  const validated = addCollectionItemInputSchema.parse({ portfolioId: folioId });
  const data = await fetchWithEnvelope<unknown>(`/collections/${id}/items`, {
    method: 'POST',
    body: JSON.stringify(validated),
  });
  return collectionItemPreviewSchema.parse(data);
}

/**
 * [목적] GET /collections/:id/items — 컬렉션 아이템 목록 (cursor 페이지네이션)
 * [이유] INFRA-06: CollectionPage 아이템 무한 스크롤에서 호출. 비인증 허용
 * [사이드 이팩트] private/link 폴리오는 비로그인 viewer 에게 숨김 (BE Q-20 후속)
 * [수정 시 고려사항] cursor/limit 기본값 20, 최대 50 (BE 서버사이드 제한)
 */
export async function listItems(
  id: string,
  q?: { cursor?: string; limit?: number },
): Promise<CollectionItemPage> {
  const query = buildQuery({ cursor: q?.cursor, limit: q?.limit });
  const data = await fetchWithEnvelope<unknown>(`/collections/${id}/items${query}`);
  return collectionItemPageSchema.parse(data);
}

/**
 * [목적] DELETE /collections/:id/items/:folioId — 폴리오 제거 (204 반환)
 * [이유] INFRA-06: 컬렉션에서 폴리오 제거 버튼에서 호출 (후속 UI 배선)
 * [사이드 이팩트] JwtAuthGuard + CsrfGuard 필수. 멤버십 없으면 idempotent 204
 * [수정 시 고려사항] folioId UUID 형식 필수 (BE ParseUUIDPipe)
 */
export async function removeItem(id: string, folioId: string): Promise<void> {
  await fetchWithEnvelope<void>(`/collections/${id}/items/${folioId}`, { method: 'DELETE' });
}
